import { ClassValue, clsx } from 'clsx';
import { CSSProperties, FunctionComponent, SVGProps, useState } from 'react';
import { resolveSvgComponent } from './resolveSvgComponent';
import classNames from './Image.module.css';

// SVG imports via vite-plugin-svgr return a React component directly
type SvgComponent = FunctionComponent<SVGProps<SVGSVGElement>>;

export type ImageSource = { uri?: string } | SvgComponent;

export interface ImageProps {
  source?: ImageSource | null;
  className?: ClassValue;
  /** Per-instance values only; style with `className`. */
  style?: CSSProperties;
  /** Grow to fill the parent along both axes. */
  fill?: boolean;
  resizeMode?: 'cover' | 'contain';
  /** Passed to SVG sources as `color`. Photos aren't tinted. */
  tintColor?: string;
  onLoad?: VoidFunction;
  onError?: VoidFunction;
  /** Omit for decorative images. */
  alt?: string;
  testID?: string;
}

export function Image({
  source,
  className,
  style,
  fill,
  resizeMode = 'cover',
  tintColor,
  onLoad,
  onError,
  alt,
  testID,
}: ImageProps) {
  const [failedUri, setFailedUri] = useState<string>();

  const classes = clsx(
    classNames.image,
    fill && classNames.fill,
    resizeMode === 'contain' && classNames.contain,
    className,
  );

  const Svg = resolveSvgComponent(source);
  if (Svg) {
    // The wrapper takes the sizing classes: SVGR gives every SVG a 2.5em width and height, which
    // would stop the element itself from stretching under `fill`
    return (
      <div
        className={classes}
        style={style}
        role={alt ? 'img' : undefined}
        aria-label={alt || undefined}
        aria-hidden={alt ? undefined : true}
        data-testid={testID}
      >
        <Svg className={classNames.svg} color={tintColor} />
      </div>
    );
  }

  const uri = source && 'uri' in source ? source.uri : undefined;
  const hasFailed = !!uri && uri === failedUri;

  const handleError = () => {
    setFailedUri(uri);
    onError?.();
  };

  // An `<img>` with no source or a failed one shows the browser's broken-image icon, where React
  // Native Web showed nothing. Keep the box, its size and background, without the icon.
  if (!uri || hasFailed) {
    return <div className={classes} style={style} data-testid={testID} />;
  }

  return (
    <img
      className={classes}
      style={style}
      src={uri}
      alt={alt ?? ''}
      onLoad={onLoad}
      onError={handleError}
      data-testid={testID}
    />
  );
}
